import { Alert, Button, Segmented, Spin, Tag, Typography } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ExportOutlined } from '@ant-design/icons';
import { useAuthenticatedRequest, UserContext } from '../../UserContext';
import { ClientConfig } from '../../types/config';
import { EngagementTimeline } from './engagement-timeline';
import { eventColor, eventLabel } from '../../Insights/LeadEngagementLedger';
import { LeadFacts } from '../../Insights/LeadFacts';
import { getAgentTimezone, getCrmLink } from '../agentSms/utils';
import { formatDateWithZone } from './format-date';
import { isOverdue, relativeFromNow } from './relative-time';
import { LeadEngagement, reengageLead } from './lead-engagements-page';
import './lead-engagement-detail-page.css';

type ContextTab = 'timeline' | 'facts';

const TAB_OPTIONS: { label: string; value: ContextTab }[] = [
  { label: 'Timeline', value: 'timeline' },
  { label: 'Lead facts', value: 'facts' },
];

const RailField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="led-rail-field">
    <div className="led-rail-label">{label}</div>
    <div className="led-rail-value">{value}</div>
  </div>
);

export const LeadEngagementDetailPage = ({ apiUrl }: { apiUrl: string }) => {
  const { clientId, leadId } = useParams<{
    clientId: string;
    leadId: string;
  }>();
  const user = useContext(UserContext);
  const authenticatedRequest = useAuthenticatedRequest(user?.accessToken);

  const [engagement, setEngagement] = useState<LeadEngagement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientConfig, setClientConfig] = useState<ClientConfig | null>(null);
  const [tab, setTab] = useState<ContextTab>('timeline');
  const [toggleLoading, setToggleLoading] = useState(false);
  const [reengageLoading, setReengageLoading] = useState(false);

  useEffect(() => {
    if (!clientId || !leadId) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void authenticatedRequest(headers =>
      axios.get(`${apiUrl}/api/lead-engagements/${clientId}`, {
        params: { page: 1, page_size: 1, search: '', lead_id: leadId },
        headers,
      }),
    )
      .then(response => {
        if (cancelled) {
          return;
        }
        const items = response.data?.items;
        if (items?.length > 0) {
          setEngagement(items[0]);
        } else {
          setError('No engagement found for this lead.');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load this engagement.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, clientId, leadId, user?.accessToken]);

  useEffect(() => {
    if (!clientId) {
      return;
    }
    void authenticatedRequest(headers =>
      axios.get(`${apiUrl}/api/configs/${clientId}`, { headers }),
    )
      .then(response => setClientConfig(response.data))
      .catch(() => setClientConfig(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, clientId, user?.accessToken]);

  if (loading) {
    return (
      <div className="led-page led-centered">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !engagement || !clientId) {
    return (
      <div className="led-page" style={{ padding: 24 }}>
        <Alert
          message={error || 'Engagement not found'}
          showIcon
          type="error"
        />
      </div>
    );
  }

  const lead = engagement.lead;
  const fullAgent = lead?.agent
    ? clientConfig?.agents?.find(a => a.id === lead.agent?.id) || lead.agent
    : undefined;
  const timezone = getAgentTimezone(fullAgent as any, clientConfig);
  const crmLink = getCrmLink(clientConfig, lead?.source_id);
  const fullName = lead
    ? `${lead.first_name} ${lead.last_name}`.trim() || 'Unknown lead'
    : 'Unknown lead';
  const initials = lead
    ? `${lead.first_name?.[0] || ''}${lead.last_name?.[0] || ''}`.toUpperCase()
    : '?';

  const handleToggleActive = async () => {
    const nextActive = !engagement.is_active;
    setToggleLoading(true);
    try {
      await authenticatedRequest(headers =>
        axios.patch(
          `${apiUrl}/api/lead-engagement/${engagement.id}/toggle-active`,
          { toggle: nextActive },
          { headers },
        ),
      );
      setEngagement((prev: LeadEngagement | null) =>
        prev ? { ...prev, is_active: nextActive } : prev,
      );
    } catch (err) {
      console.error('Failed to toggle engagement:', err);
    } finally {
      setToggleLoading(false);
    }
  };

  const handleReengage = async () => {
    setReengageLoading(true);
    try {
      await authenticatedRequest(headers =>
        reengageLead(apiUrl, engagement.lead_id, headers),
      );
    } catch (err) {
      console.error('Failed to re-engage lead:', err);
    } finally {
      setReengageLoading(false);
    }
  };

  return (
    <div className="led-page">
      <div className="led-breadcrumb">
        <Link to={`/lead-engagements/${clientId}`}>
          {clientConfig?.friendly_name || `Client ${clientId}`}
        </Link>
        <span>/</span>
        <span>Engagements</span>
        <span>/</span>
        <span className="led-breadcrumb-current">{fullName}</span>
      </div>

      <div className="led-layout">
        <aside className="led-rail">
          <div className="led-rail-identity">
            <div className="led-avatar">{initials}</div>
            <div>
              <div className="led-name">{fullName}</div>
              <div className="led-phone">
                {engagement.phone_number || lead?.phone || '—'}
              </div>
            </div>
          </div>

          <div className="led-tags">
            <Tag color={engagement.is_active ? 'success' : 'default'}>
              {engagement.is_active ? '● Active' : 'Inactive'}
            </Tag>
            {engagement.ledger_state && (
              <Tag color={eventColor(engagement.ledger_state)}>
                {eventLabel(engagement.ledger_state)}
              </Tag>
            )}
          </div>

          <div className="led-actions">
            {crmLink && (
              <Button
                block
                href={crmLink}
                icon={<ExportOutlined />}
                size="small"
                target="_blank"
              >
                View in CRM
              </Button>
            )}
            <Button
              block
              loading={toggleLoading}
              onClick={() => void handleToggleActive()}
              size="small"
            >
              {engagement.is_active ? 'Deactivate' : 'Reactivate'}
            </Button>
            {/* Sends immediate outreach — kept visually quiet so it isn't the reflexive click. */}
            <Button
              block
              loading={reengageLoading}
              onClick={() => void handleReengage()}
              size="small"
              style={{ color: '#8c8c8c' }}
              type="text"
            >
              Re-evaluate / re-send
            </Button>
          </div>

          <RailField label="Lead type" value={engagement.lead_type || '—'} />
          <RailField
            label="Preferred language"
            value={engagement.preferred_language || '—'}
          />
          <RailField
            label="Zip code"
            value={engagement.engagement_zip_code || '—'}
          />
          <RailField
            label="Action plan"
            value={engagement.action_plan_name || '—'}
          />
        </aside>

        <main className="led-main">
          <Segmented
            onChange={value => setTab(value as ContextTab)}
            options={TAB_OPTIONS}
            size="middle"
            value={tab}
          />
          <div className="led-main-content">
            {tab === 'timeline' && (
              <EngagementTimeline
                accessToken={user?.accessToken || ''}
                agentId={lead?.agent?.id}
                apiUrl={apiUrl}
                campaignKey={engagement.campaign_key}
                clientId={clientId}
                crmLink={crmLink}
                engagementId={engagement.id}
                leadId={engagement.lead_id}
                sourceId={lead?.source_id}
                timezone={timezone}
              />
            )}
            {tab === 'facts' && (
              <div className="led-card led-facts-card">
                <LeadFacts
                  apiUrl={apiUrl}
                  leadId={engagement.lead_id}
                  timezone={timezone}
                />
              </div>
            )}
          </div>
        </main>

        <aside className="led-sidebar">
          <div className="led-card">
            <div className="led-card-title">Engagement details</div>
            <RailField
              label="Cadence"
              value={
                <>
                  {engagement.stage || '—'}
                  {engagement.outreach_channel && (
                    <span className="led-rail-muted">
                      {' '}
                      · {engagement.outreach_channel}
                    </span>
                  )}
                </>
              }
            />
            <RailField
              label="Next evaluation"
              value={
                engagement.next_evaluation_at ? (
                  <>
                    <span
                      style={
                        isOverdue(engagement.next_evaluation_at)
                          ? { color: '#d4380d' }
                          : undefined
                      }
                    >
                      {relativeFromNow(engagement.next_evaluation_at)}
                    </span>
                    <span className="led-rail-value-sub">
                      {' · '}
                      {formatDateWithZone(
                        engagement.next_evaluation_at,
                        timezone,
                      )}
                    </span>
                  </>
                ) : (
                  '—'
                )
              }
            />
            <RailField
              label="Campaign"
              value={engagement.campaign_key || '—'}
            />
            <RailField
              label="Replies / AI sent"
              value={`${engagement.message_count ?? 0} / ${engagement.ai_message_count ?? 0}`}
            />
            <RailField
              label="Created"
              value={
                <>
                  <span>{relativeFromNow(engagement.created_at)}</span>
                  <span className="led-rail-value-sub">
                    {' · '}
                    {formatDateWithZone(engagement.created_at, timezone)}
                  </span>
                </>
              }
            />
            {lead?.source_id && (
              <RailField
                label="Source ID"
                value={
                  <Typography.Text copyable style={{ fontSize: 12 }}>
                    {lead.source_id}
                  </Typography.Text>
                }
              />
            )}
          </div>

          {lead?.email && (
            <div className="led-card">
              <div className="led-card-title">Contact</div>
              <RailField
                label="Email"
                value={
                  <Typography.Text copyable style={{ fontSize: 12 }}>
                    {lead.email}
                  </Typography.Text>
                }
              />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};
